import React, { useLayoutEffect, useRef, useState } from "react";

// Idle-triggered coach-mark. Unlike the linear tour (right-docked ChatPanel +
// dimmed spotlight), this is a self-contained, NON-blocking bubble: a soft ring
// around the element + a small card carrying the step copy and its own dismiss
// controls. No screen dim, no side panel — a gentle "here's a tip", not a takeover.
const PAD = 6;   // ring inset, matches CoachBubble
const GAP = 14;  // distance from the ring to the card
const MARGIN = 8; // keep the card this far inside the viewport

const JitBubble = ({ rect, step, onGotIt, onStop }) => {
  const cardRef = useRef(null);
  const [pos, setPos] = useState(null);

  const insideModal = !!(step && step.insideModal);
  const z = insideModal ? 1460 : 1310;
  const side = (step && step.side) || "bottom";

  // <html> has `zoom: 0.8` at desktop widths. getBoundingClientRect() returns
  // post-zoom coords while CSS top/left get scaled by zoom AGAIN — divide the rect
  // by the live zoom so fixed elements land on target (same fix as CoachBubble).
  const zoom =
    (typeof window !== "undefined" &&
      parseFloat(getComputedStyle(document.documentElement).zoom)) || 1;
  const r = rect
    ? { top: rect.top / zoom, left: rect.left / zoom, width: rect.width / zoom, height: rect.height / zoom }
    : null;

  // Position the card beside the element on `side`, then clamp it into the
  // viewport. Measured after layout so we know the card's real size.
  useLayoutEffect(() => {
    if (!r || !cardRef.current) return;
    const cw = cardRef.current.offsetWidth;
    const ch = cardRef.current.offsetHeight;
    const vw = window.innerWidth / zoom;
    const vh = window.innerHeight / zoom;

    let top;
    let left;
    if (side === "top") { top = r.top - PAD - GAP - ch; left = r.left + r.width / 2 - cw / 2; }
    else if (side === "left") { left = r.left - PAD - GAP - cw; top = r.top + r.height / 2 - ch / 2; }
    else if (side === "right") { left = r.left + r.width + PAD + GAP; top = r.top + r.height / 2 - ch / 2; }
    else { top = r.top + r.height + PAD + GAP; left = r.left + r.width / 2 - cw / 2; }

    left = Math.max(MARGIN, Math.min(left, vw - cw - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - ch - MARGIN));
    setPos({ top, left });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r && r.top, r && r.left, r && r.width, r && r.height, side, step && step.id]);

  if (!r) return null;

  const ringStyle = {
    position: "fixed",
    top: r.top - PAD,
    left: r.left - PAD,
    width: r.width + PAD * 2,
    height: r.height + PAD * 2,
    borderRadius: 8,
    // Soft ring ONLY — no full-screen dimming box-shadow.
    boxShadow: "0 0 0 3px #6B43B5",
    zIndex: z,
    pointerEvents: "none",
    transition: "all 140ms ease",
  };

  // Hidden until measured (pos) so it doesn't flash at the top-left corner.
  const cardStyle = {
    position: "fixed",
    zIndex: z + 1,
    top: pos ? pos.top : -9999,
    left: pos ? pos.left : -9999,
    visibility: pos ? "visible" : "hidden",
  };

  return (
    <>
      <div className="go-jit-ring" style={ringStyle} aria-hidden="true" />
      <div className="go-jit-card" style={cardStyle} ref={cardRef} role="dialog" aria-label={step.title}>
        <div className="go-jit-title">{step.title}</div>
        <div className="go-jit-body">{step.body}</div>
        <div className="go-jit-actions">
          <button type="button" className="go-jit-stop" onClick={onStop}>Don't show tips</button>
          <button type="button" className="go-jit-got" onClick={onGotIt}>Got it</button>
        </div>
      </div>
    </>
  );
};

export default JitBubble;
